'use client';

import type { ReactNode, Reducer } from 'react';
import { createContext, useContext, useMemo, useReducer } from 'react';

type UploadLinkModalState = {
  uploadLinkId: string | null;
  folderId: string | null;
  autoCopiedUrl: string | null;
};

type UploadLinkModalOptions = {
  /** The id of the folder the link belongs to, required to query the link. */
  folderId: string;
  autoCopiedUrl?: string;
};

type UploadLinkModalAction =
  | ({ type: 'OPEN_MODAL'; uploadLinkId: string } & UploadLinkModalOptions)
  | { type: 'CLOSE_MODAL' };

const reducer: Reducer<UploadLinkModalState, UploadLinkModalAction> = (
  state,
  action
) => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return {
        uploadLinkId: action.uploadLinkId,
        folderId: action.folderId,
        autoCopiedUrl: action.autoCopiedUrl ?? null,
      };
    case 'CLOSE_MODAL':
      return { uploadLinkId: null, folderId: null, autoCopiedUrl: null };
    default:
      return state;
  }
};

const DEFAULT_STATE: UploadLinkModalState = {
  uploadLinkId: null,
  folderId: null,
  autoCopiedUrl: null,
};

type UploadLinkModalContextValue = {
  isOpen: boolean;
  uploadLinkId: string | null;
  folderId: string | null;
  autoCopiedUrl: string | null;
  open: (uploadLinkId: string, options: UploadLinkModalOptions) => void;
  close: () => void;
};

const UploadLinkModalContext =
  createContext<UploadLinkModalContextValue | null>(null);

export function useUploadLinkModal(): UploadLinkModalContextValue {
  const ctx = useContext(UploadLinkModalContext);
  if (!ctx) {
    throw new Error(
      'useUploadLinkModal must be used within an UploadLinkModalProvider'
    );
  }
  return ctx;
}

export function UploadLinkModalProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE);

  const value = useMemo<UploadLinkModalContextValue>(
    () => ({
      isOpen: state.uploadLinkId !== null,
      uploadLinkId: state.uploadLinkId,
      folderId: state.folderId,
      autoCopiedUrl: state.autoCopiedUrl,
      open: (uploadLinkId, { folderId, autoCopiedUrl }) =>
        dispatch({ type: 'OPEN_MODAL', uploadLinkId, folderId, autoCopiedUrl }),
      close: () => dispatch({ type: 'CLOSE_MODAL' }),
    }),
    [state.uploadLinkId, state.folderId, state.autoCopiedUrl]
  );

  return (
    <UploadLinkModalContext.Provider value={value}>
      {children}
    </UploadLinkModalContext.Provider>
  );
}
